/**
 * ATT Stringing — cart integrity (contract v2)
 * ---------------------------------------------------------------------------
 * Keeps racket lines and their stringing service lines in step.
 *
 *   - removing the racket removes its stringing line(s)
 *   - removing the stringing line strips the pairing token off the racket, so
 *     it reverts to unstrung rather than being strung for free
 *   - quantities follow the racket, multiplied by how many rackets that line
 *     contains (2 for a bundle of two frames)
 *
 * Handles both contract versions. In v1 the spec lived on the racket line; from
 * v2 it lives on the stringing service line, because a native bundle's parent
 * line is replaced by its components at order creation and everything attached
 * to it is discarded. Carts created before the change are still in flight, so
 * both shapes are supported.
 *
 * Approach is reconcile-after rather than intercept-before: Prestige renders
 * cart controls as plain links that reload the page, and the drawer re-renders
 * over Ajax, so reconciling against /cart.js after any mutation covers every
 * path including other apps and the back button.
 *
 * This is the convenience layer. The Shopify Function is the guarantee —
 * accelerated checkout never touches this page at all.
 *
 * Load on every page, e.g. before </body> in layout/theme.liquid:
 *   <script src="{{ 'stringing-cart.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  var REF = '_stringing_ref';
  var TIER = '_stringing_tier';
  var UNITS = '_racket_units';
  var PER_UNIT = '_rackets_per_unit';
  var VERSION = '_spec_version';

  var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  var reconciling = false;

  // ---------------------------------------------------------------------------
  // Reading the cart
  // ---------------------------------------------------------------------------

  function getCart() {
    return fetch(root + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function props(item) {
    return item.properties || {};
  }

  function refOf(item) {
    return props(item)[REF] || null;
  }

  function versionOf(item) {
    return props(item)[VERSION] || '1';
  }

  /**
   * Which side of the pair is this line?
   *
   * v2 marks each side explicitly: the racket carries _rackets_per_unit, the
   * service carries _racket_units. v1 had no marker, so the presence of the
   * tier attribute decides — and it means the opposite thing there, which is
   * exactly why v2 added explicit markers.
   */
  function classify(item) {
    if (!refOf(item)) return null;
    var p = props(item);

    if (Object.prototype.hasOwnProperty.call(p, UNITS)) return 'service';
    if (Object.prototype.hasOwnProperty.call(p, PER_UNIT)) return 'racket';

    // v1 shape
    return p[TIER] ? 'racket' : 'service';
  }

  function racketsPerUnit(racket) {
    var n = parseInt(props(racket)[PER_UNIT], 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function unitsFor(service) {
    var n = parseInt(props(service)[UNITS], 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function pairs(cart) {
    var map = {};
    cart.items.forEach(function (item) {
      var ref = refOf(item);
      if (!ref) return;
      if (!map[ref]) map[ref] = { ref: ref, racket: null, services: [] };
      var role = classify(item);
      if (role === 'racket') {
        map[ref].racket = item;
      } else if (role === 'service') {
        map[ref].services.push(item);
      }
    });
    return Object.keys(map).map(function (k) { return map[k]; });
  }

  // ---------------------------------------------------------------------------
  // Reconciliation
  // ---------------------------------------------------------------------------

  function plan(cart) {
    var actions = [];

    pairs(cart).forEach(function (pair) {
      if (!pair.racket) {
        pair.services.forEach(function (service) {
          actions.push({ type: 'remove', key: service.key, reason: 'racket removed' });
        });
        return;
      }

      if (!pair.services.length) {
        actions.push({ type: 'strip', key: pair.racket.key, reason: 'stringing removed' });
        return;
      }

      var perUnit = racketsPerUnit(pair.racket);

      pair.services.forEach(function (service) {
        // A v1 service line has no units marker and covers exactly one racket.
        var units = versionOf(service) === '1' ? 1 : unitsFor(service);
        var expected = pair.racket.quantity * units;

        // Guard against a nonsensical spread: units should never exceed the
        // rackets the line actually contains.
        if (units > perUnit) {
          actions.push({ type: 'remove', key: service.key, reason: 'more stringing than rackets' });
          return;
        }

        if (service.quantity !== expected) {
          actions.push({
            type: 'quantity',
            key: service.key,
            quantity: expected,
            reason: 'racket quantity changed'
          });
        }
      });
    });

    return actions;
  }

  function isStringingKey(key) {
    if (key === REF) return true;
    if (key.indexOf('_stringing') === 0) return true;
    if (key.indexOf('_string_') === 0) return true;
    if (key.indexOf('_mains') === 0) return true;
    if (key.indexOf('_crosses') === 0) return true;
    if (key === '_split_bed' || key === '_tension' || key === VERSION) return true;
    if (key === UNITS || key === PER_UNIT || key === '_racket_index') return true;
    if (key === 'Stringing' || key === 'String' || key === 'Tension') return true;
    if (key === 'Mains' || key === 'Crosses') return true;
    if (key === 'Mains tension' || key === 'Crosses tension') return true;
    if (key === 'Racket' || key === 'For') return true;
    return false;
  }

  /**
   * Properties to send to /cart/change.js to drop the stringing spec from a
   * line. Non-stringing properties pass through unchanged and every stringing
   * key is sent as an empty string — Shopify drops empty-valued properties, so
   * this works whether change.js replaces the property set or merges into it.
   */
  function cleanedProperties(item) {
    var current = props(item);
    var next = {};
    Object.keys(current).forEach(function (key) {
      next[key] = isStringingKey(key) ? '' : current[key];
    });
    return next;
  }

  function change(body) {
    return fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function execute(actions, cart) {
    // Sequential, because each change invalidates the line keys that follow.
    return actions.reduce(function (chain, action) {
      return chain.then(function () {
        if (action.type === 'remove') return change({ id: action.key, quantity: 0 });
        if (action.type === 'quantity') return change({ id: action.key, quantity: action.quantity });
        if (action.type === 'strip') {
          var item = cart.items.filter(function (i) { return i.key === action.key; })[0];
          if (!item) return null;
          return change({ id: action.key, quantity: item.quantity, properties: cleanedProperties(item) });
        }
        return null;
      });
    }, Promise.resolve());
  }

  function reconcile() {
    if (reconciling) return Promise.resolve(false);
    reconciling = true;

    return getCart()
      .then(function (cart) {
        var actions = plan(cart);
        if (!actions.length) return false;
        console.info('[stringing] reconciling cart', actions);
        return execute(actions, cart).then(function () { return true; });
      })
      .then(function (changed) {
        reconciling = false;
        if (changed) refreshCart();
        return changed;
      })
      .catch(function (error) {
        reconciling = false;
        console.error('[stringing] reconcile failed', error);
        return false;
      });
  }

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (window.location.pathname.indexOf('/cart') === 0) window.location.reload();
  }

  // ---------------------------------------------------------------------------
  // Presentation
  // ---------------------------------------------------------------------------

  function decorate(cart) {
    var serviceKeys = cart.items
      .filter(function (i) { return classify(i) === 'service'; })
      .map(function (i) { return i.key; });
    if (!serviceKeys.length) return;

    serviceKeys.forEach(function (key) {
      var input = document.querySelector('[data-line-key="' + cssEscape(key) + '"]');
      if (!input) return;

      var row = input.closest('tr') || input.closest('line-item') || input.parentElement;
      if (!row) return;

      row.querySelectorAll('line-item-quantity').forEach(function (node) {
        node.innerHTML = '<span class="text-subdued text-xs">Follows the racket</span>';
      });

      row.querySelectorAll('a[href*="/cart/change"]').forEach(function (link) {
        link.setAttribute('aria-disabled', 'true');
        link.style.pointerEvents = 'none';
        link.style.opacity = '0.4';
      });
    });
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  // ---------------------------------------------------------------------------
  // Triggers
  // ---------------------------------------------------------------------------

  var nativeFetch = window.fetch;
  window.fetch = function () {
    var request = arguments[0];
    var url = typeof request === 'string' ? request : (request && request.url) || '';
    var mutating = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);

    var result = nativeFetch.apply(this, arguments);

    if (mutating && !reconciling) {
      result.then(function (response) {
        if (!response.ok) return;
        setTimeout(function () {
          reconcile().then(function (changed) {
            if (!changed) getCart().then(decorate);
          });
        }, 60);
      }).catch(function () { /* the caller handles its own failure */ });
    }

    return result;
  };

  function init() {
    reconcile().then(function (changed) {
      if (!changed) getCart().then(decorate);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('cart:refresh', function () {
    if (reconciling) return;
    setTimeout(function () { getCart().then(decorate); }, 120);
  });

  window.ATTStringingCart = { reconcile: reconcile, plan: plan, getCart: getCart, classify: classify };
})();